import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDialogState } from '@user/utils/useDialogState'
import { useUpdateLeadStatus } from '@user/store/lead'
import { IMoreMenuItem, toast } from '@sales-center/components'
import { permissionDenied } from '@user/store/authorization/permissions'
import { ReOpenLeadDialogProps } from '../../ReOpenDialog/ReOpenLeadDialog'
import { ISalesFunnelStage } from '@user/store/salesFunnel'
import { LeadParentHeaderProps } from '../typings'
import { getSalesTeam, getSalesTeamUsers } from '@user/store/salesTeam'
import { useAppDispatch } from '@user/store/hooks'
import { updateLoadingState } from '@sales-center/common'

type Params = LeadParentHeaderProps & {
  salesFunnelStages: ISalesFunnelStage[]
  isDisqualified: boolean
}

type Result = ReOpenLeadDialogProps & {
  reopenMenuItem: IMoreMenuItem
  salesTeamLoadingStatus: 'pending' | 'fail' | 'success'
}

export function useReopenLeadMenuItem(params: Params): Result {
  const {
    lead: { id, salesFunnelId, salesFunnelStageId, salesTeamId, assigneeId },
    canUpdate,
    salesFunnelStages,
    isDisqualified,
  } = params
  const dispatch = useAppDispatch()
  const { execute } = useUpdateLeadStatus()

  const [open, onOpen, onClose] = useDialogState()
  const [salesTeamLoadingStatus, setSalesTeamLoadingStatus] = useState<
    'pending' | 'fail' | 'success'
  >('pending')

  const handleSubmit = useCallback(
    (stageId: number, owner?: string | null) => {
      const stage = salesFunnelStages.find((x) => x.id === stageId)
      if (!!stage) {
        execute({
          leadId: id,
          reasonId: 0,
          disqualifiedDate: null,
          customReason: '',
          salesFunnelStageId: stage.id,
          salesFunnelStageName: stage.name,
          owner,
        })
      }
      onClose()
    },
    [execute, id, onClose, salesFunnelStages],
  )

  const reopenMenuItem = useMemo<IMoreMenuItem>(
    () => ({
      dataTestId: `reopenBtn-${id}`,
      label: 'Re-Open',
      onClick: onOpen,
      hidden: !isDisqualified,
      disabled: !canUpdate,
      tooltipText: canUpdate ? undefined : permissionDenied,
    }),
    [canUpdate, id, isDisqualified, onOpen],
  )

  useEffect(() => {
    if (open) {
      const execute = async () => {
        try {
          setSalesTeamLoadingStatus('pending')
          dispatch(updateLoadingState('visible'))
          await dispatch(getSalesTeam({ id: salesTeamId, includeUsers: true }))
          const result = await dispatch(getSalesTeamUsers(salesTeamId))
          if (getSalesTeamUsers.fulfilled.match(result)) {
            setSalesTeamLoadingStatus('success')
          } else {
            setSalesTeamLoadingStatus('fail')
            toast.error(`Error getting sales team users`)
          }
        } catch (error) {
          setSalesTeamLoadingStatus('fail')
          toast.error(`Error getting sales team users`)
        } finally {
          dispatch(updateLoadingState('hidden'))
        }
      }

      execute()
    }
  }, [dispatch, open, salesTeamId])

  return {
    reopenMenuItem,
    open,
    onClose,
    handleSubmit,
    salesFunnelId,
    salesFunnelStageId,
    salesTeamId: salesTeamId.toString(),
    assigneeId,
    salesTeamLoadingStatus,
  }
}
